import { useState, useMemo, useCallback } from "react";
import {
  Stack,
  SimpleGrid,
  MultiSelect,
  Checkbox,
  TextInput,
  Select,
  Group,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconCalendar, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { parseDateString, formatDateForApi } from "../../utils/dates";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";
import {
  useFacilities,
  usePathologists,
  usePhysicians,
  useAccounts,
  useLabs,
  useSurgeryCenters,
} from "../../hooks/useReferenceData";
import { useCaseStatusesForFiltering } from "../../hooks/useAdmin";
import {
  workflowsService,
  patientsService,
  type CaseFilters as CaseFiltersType,
} from "../../services";
import { proceduresService } from "../../services/admin/procedures.service";
import { flagDefinitionsService } from "../../services/flagDefinitionsService";
import AdditionalStainFilter from "./AdditionalStainFilter";
import UnavailablePrefixChip from "./UnavailablePrefixChip";

interface CaseFiltersProps {
  filters: CaseFiltersType;
  onFiltersChange: (filters: CaseFiltersType) => void;
  /** Optional list of status names to show. If not provided, all statuses except "Draft" are shown. Pass ["*"] to show all including Draft. */
  allowedStatusNames?: string[];
  /** Show extra client-side filters for the pathologist review queue (Case Type, TAT, Pending Tests) */
  showQueueFilters?: boolean;
  /** Show extra client-side filters for the incoming lab queue (Current Step) */
  showIncomingFilters?: boolean;
  showAdditionalStainFilter?: boolean;
  showRelationshipFilters?: boolean;
  pathologistFilterMode?: "editable" | "assigned-user";
  prefixOptions?: string[];
}

// Shared styles for MultiSelect - single line with horizontal scroll
const multiSelectStyles = {
  input: {
    minHeight: 36,
    height: 36,
    overflow: "hidden",
  },
  pillsList: {
    flexWrap: "nowrap" as const,
    overflow: "auto",
  },
};

export function formatPatientFilterLabel(patient: {
  firstName: string;
  lastName: string;
  dateOfBirth?: unknown;
}): string {
  const dateOfBirth = parseDateOnly(patient.dateOfBirth);
  const displayedDateOfBirth = dateOfBirth
    ? ` (${formatDateOnlyForDisplay(dateOfBirth)})`
    : "";
  return `${patient.lastName}, ${patient.firstName}${displayedDateOfBirth}`;
}

export default function CaseFilters({
  filters,
  onFiltersChange,
  allowedStatusNames,
  showQueueFilters = false,
  showIncomingFilters = false,
  showAdditionalStainFilter = false,
  showRelationshipFilters = false,
  pathologistFilterMode = "editable",
  prefixOptions,
}: CaseFiltersProps) {
  const [patientSearch, setPatientSearch] = useState("");
  const [debouncedPatientSearch] = useDebouncedValue(patientSearch, 300);

  const handlePrefixKeysChange = useCallback(
    (prefixKeys: string[]) => {
      const unavailable = (filters.prefixKeys ?? []).filter(
        (key) => !prefixOptions?.includes(key)
      );
      const selected = Array.from(new Set([...unavailable, ...prefixKeys]));
      onFiltersChange({
        ...filters,
        prefixKeys: selected.length ? selected : undefined,
      });
    },
    [filters, onFiltersChange, prefixOptions]
  );
  const unavailablePrefixKeys = useMemo(
    () =>
      (filters.prefixKeys ?? []).filter((key) => !prefixOptions?.includes(key)),
    [filters.prefixKeys, prefixOptions]
  );
  const handleRemoveUnavailablePrefix = useCallback(
    (prefixKey: string) => {
      const prefixKeys = (filters.prefixKeys ?? []).filter(
        (key) => key !== prefixKey
      );
      onFiltersChange({
        ...filters,
        prefixKeys: prefixKeys.length ? prefixKeys : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const { data: facilities = [] } = useFacilities();
  const { data: practices = [] } = useAccounts({
    enabled: showRelationshipFilters,
  });
  const { data: labs = [] } = useLabs({ enabled: showRelationshipFilters });
  const { data: surgeryCenters = [] } = useSurgeryCenters({
    enabled: showRelationshipFilters,
  });
  const { data: pathologists = [] } = usePathologists();
  const { data: physicians = [] } = usePhysicians();
  const { data: workflows = [] } = useQuery({
    queryKey: ["workflows"],
    queryFn: workflowsService.getAll,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const { data: statuses = [] } = useCaseStatusesForFiltering();
  const { data: procedures = [] } = useQuery({
    queryKey: ["procedures"],
    queryFn: proceduresService.getAll,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const { data: activeFlags = [] } = useQuery({
    queryKey: ["activeFlagDefinitions"],
    queryFn: () => flagDefinitionsService.getActive(),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const { data: searchedPatients = [] } = useQuery({
    queryKey: ["patients-search", debouncedPatientSearch],
    queryFn: () =>
      debouncedPatientSearch.length >= 2
        ? patientsService.search(debouncedPatientSearch)
        : Promise.resolve([]),
    enabled: debouncedPatientSearch.length >= 2,
  });

  // Fetch selected patients that might not be in search results
  const { data: selectedPatients = [] } = useQuery({
    queryKey: ["patients-selected", filters.patientIds],
    queryFn: async () => {
      if (!filters.patientIds?.length) return [];
      const patients = await Promise.all(
        filters.patientIds.map((id) =>
          patientsService.getById(id).catch(() => null)
        )
      );
      return patients.filter(Boolean);
    },
    enabled: !!filters.patientIds?.length,
  });

  // Combine searched patients with selected patients for the dropdown
  const patientOptions = useMemo(() => {
    const allPatients = [...searchedPatients];
    // Add selected patients that aren't in search results
    selectedPatients.forEach((p) => {
      if (p && !allPatients.find((sp) => sp.id === p.id)) {
        allPatients.push(p);
      }
    });
    return allPatients.map((p) => ({
      value: p.id,
      label: formatPatientFilterLabel(p),
    }));
  }, [searchedPatients, selectedPatients]);

  const handlePracticeChange = useCallback(
    (values: string[]) => {
      onFiltersChange({
        ...filters,
        sendingPracticeId: values.length ? values : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const handleSendingLabChange = useCallback(
    (values: string[]) => {
      onFiltersChange({
        ...filters,
        submittingLabId: values.length ? values : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  const handleSurgicalCenterChange = useCallback(
    (values: string[]) => {
      onFiltersChange({
        ...filters,
        surgeryCenterLabId: values.length ? values : undefined,
      });
    },
    [filters, onFiltersChange]
  );

  // Order matches table columns: Case Number, Patient, Procedure, Date Received, Status, Workflow, Pathologist, Facility
  return (
    <Stack gap="md">
      <TextInput
        placeholder="Search by case # or external #..."
        leftSection={<IconSearch size={16} />}
        value={filters.search || ""}
        onChange={(e) =>
          onFiltersChange({ ...filters, search: e.target.value })
        }
      />

      {prefixOptions && (
        <Checkbox.Group
          label="Case number prefixes"
          description="Matches any selected prefix"
          value={filters.prefixKeys ?? []}
          onChange={handlePrefixKeysChange}
        >
          <Group gap="sm" mt="xs">
            {prefixOptions.map((prefixKey) => (
              <Checkbox
                key={prefixKey}
                value={prefixKey}
                label={
                  prefixKey === "__OTHER__"
                    ? "Other / nonstandard number"
                    : prefixKey
                }
                size="sm"
              />
            ))}
          </Group>
        </Checkbox.Group>
      )}
      {prefixOptions && unavailablePrefixKeys.length > 0 && (
        <Stack gap="xs">
          <Text size="sm">Unavailable selected prefixes</Text>
          <Group gap="xs">
            {unavailablePrefixKeys.map((prefixKey) => (
              <UnavailablePrefixChip
                key={prefixKey}
                prefixKey={prefixKey}
                onRemove={handleRemoveUnavailablePrefix}
              />
            ))}
          </Group>
        </Stack>
      )}

      {/* Row 1: Patient | Procedure */}
      <SimpleGrid cols={2} spacing="md">
        <MultiSelect
          label="Patient"
          placeholder="Type to search patients..."
          data={patientOptions}
          value={filters.patientIds || []}
          onChange={(values) =>
            onFiltersChange({
              ...filters,
              patientIds: values.length ? values : undefined,
            })
          }
          onSearchChange={setPatientSearch}
          searchValue={patientSearch}
          searchable
          clearable
          hidePickedOptions
          filter={({ options }) => options}
          nothingFoundMessage={
            patientSearch.length < 2
              ? "Type at least 2 characters to search"
              : "No patients found"
          }
          styles={multiSelectStyles}
        />

        <MultiSelect
          label="Procedure"
          placeholder="All procedures"
          data={(procedures || [])
            .filter((p) => p.id && p.name)
            .map((p) => ({ value: p.id, label: p.name }))}
          value={filters.procedureIds || []}
          onChange={(values) =>
            onFiltersChange({
              ...filters,
              procedureIds: values.length ? values : undefined,
            })
          }
          clearable
          searchable
          hidePickedOptions
          styles={multiSelectStyles}
        />
      </SimpleGrid>

      {/* Row 2: Status | Workflow */}
      <SimpleGrid cols={2} spacing="md">
        <MultiSelect
          label="Status"
          placeholder="All statuses"
          data={statuses
            .filter((s) => {
              if (!s.id || !s.name) return false;
              if (allowedStatusNames) {
                if (allowedStatusNames.includes("*")) return true;
                return allowedStatusNames.includes(s.name);
              }
              return s.name !== "Draft";
            })
            .map((s) => ({ value: s.id, label: s.name }))}
          value={filters.currentStatusIds || []}
          onChange={(values) =>
            onFiltersChange({
              ...filters,
              currentStatusIds: values.length ? values : undefined,
            })
          }
          clearable
          searchable
          hidePickedOptions
          styles={multiSelectStyles}
        />

        <MultiSelect
          label="Workflow"
          placeholder="All workflows"
          data={(workflows || [])
            .filter((w) => w.id && (w.accessioningType || w.name))
            .map((w) => ({
              value: w.id,
              label:
                w.name ||
                (w.accessioningType
                  ? w.accessioningType
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c: string) => c.toUpperCase())
                  : "Unknown"),
            }))}
          value={filters.workflowIds || []}
          onChange={(values) =>
            onFiltersChange({
              ...filters,
              workflowIds: values.length ? values : undefined,
            })
          }
          clearable
          searchable
          hidePickedOptions
          styles={multiSelectStyles}
        />
      </SimpleGrid>

      {/* Row 3: Pathologist | Facility */}
      <SimpleGrid cols={2} spacing="md">
        {pathologistFilterMode === "editable" ? (
          <MultiSelect
            label="Assigned Pathologist"
            placeholder="All pathologists"
            data={pathologists
              .filter((p) => p.id && p.name)
              .map((p) => ({ value: p.id, label: p.name }))}
            value={filters.pathologistIds || []}
            onChange={(values) =>
              onFiltersChange({
                ...filters,
                pathologistIds: values.length ? values : undefined,
              })
            }
            clearable
            searchable
            hidePickedOptions
            styles={multiSelectStyles}
          />
        ) : (
          <TextInput
            label="Assigned Pathologist"
            value="Assigned to me"
            readOnly
          />
        )}

        <MultiSelect
          label="Facility"
          placeholder="All facilities"
          data={facilities
            .filter((f) => f.id && f.name)
            .map((f) => ({ value: f.id, label: f.name }))}
          value={filters.facilityIds || []}
          onChange={(values) =>
            onFiltersChange({
              ...filters,
              facilityIds: values.length ? values : undefined,
            })
          }
          clearable
          searchable
          hidePickedOptions
          styles={multiSelectStyles}
        />
      </SimpleGrid>

      {showRelationshipFilters && (
        <>
          <SimpleGrid cols={2} spacing="md">
            <MultiSelect
              label="Practice"
              placeholder="All practices"
              data={practices.filter(
                (practice) => practice.value && practice.label
              )}
              value={filters.sendingPracticeId || []}
              onChange={handlePracticeChange}
              clearable
              searchable
              hidePickedOptions
              styles={multiSelectStyles}
            />

            <MultiSelect
              label="Sending Lab"
              placeholder="All sending labs"
              data={labs.filter((lab) => lab.value && lab.label)}
              value={filters.submittingLabId || []}
              onChange={handleSendingLabChange}
              clearable
              searchable
              hidePickedOptions
              styles={multiSelectStyles}
            />
          </SimpleGrid>

          <MultiSelect
            label="Surgical Center"
            placeholder="All surgical centers"
            data={surgeryCenters.filter(
              (center) => center.value && center.label
            )}
            value={filters.surgeryCenterLabId || []}
            onChange={handleSurgicalCenterChange}
            clearable
            searchable
            hidePickedOptions
            styles={multiSelectStyles}
          />
        </>
      )}

      {/* Row 4: Date Received From | Date Received To */}
      <SimpleGrid cols={2} spacing="md">
        <DatePickerInput
          label="Date Received From"
          placeholder="Pick date"
          leftSection={<IconCalendar size={16} />}
          value={filters.dateFrom ? parseDateString(filters.dateFrom) : null}
          onChange={(date) =>
            onFiltersChange({
              ...filters,
              dateFrom: formatDateForApi(date) ?? undefined,
            })
          }
          maxDate={new Date()}
          clearable
        />

        <DatePickerInput
          label="Date Received To"
          placeholder="Pick date"
          leftSection={<IconCalendar size={16} />}
          value={filters.dateTo ? parseDateString(filters.dateTo) : null}
          onChange={(date) =>
            onFiltersChange({
              ...filters,
              dateTo: formatDateForApi(date) ?? undefined,
            })
          }
          maxDate={new Date()}
          clearable
        />
      </SimpleGrid>

      {/* Row 5: Doctor | Collection Date */}
      <SimpleGrid cols={2} spacing="md">
        <Select
          label="Ordering Provider"
          placeholder="All doctors"
          data={physicians.map((p) => ({ value: p.value, label: p.label }))}
          value={filters.physicianId || null}
          onChange={(value) =>
            onFiltersChange({
              ...filters,
              physicianId: value || undefined,
            })
          }
          clearable
          searchable
        />

        <DatePickerInput
          label="Collection Date"
          placeholder="Pick date"
          leftSection={<IconCalendar size={16} />}
          value={
            filters.dateCollected
              ? parseDateString(filters.dateCollected)
              : null
          }
          onChange={(date) =>
            onFiltersChange({
              ...filters,
              dateCollected: formatDateForApi(date) ?? undefined,
            })
          }
          maxDate={new Date()}
          clearable
        />
      </SimpleGrid>

      {/* Row 6: Flags */}
      <MultiSelect
        label="Flags"
        placeholder="All flags"
        description="Cases with ALL selected flags"
        data={activeFlags.map((f) => ({ value: f.id, label: f.name }))}
        value={filters.flagDefinitionIds || []}
        onChange={(values) =>
          onFiltersChange({
            ...filters,
            flagDefinitionIds: values.length ? values : undefined,
          })
        }
        clearable
        searchable
        hidePickedOptions
        styles={multiSelectStyles}
      />

      {showQueueFilters && (
        <SimpleGrid cols={2} spacing="md">
          <Select
            label="Case Type"
            placeholder="All types"
            data={[
              { value: "Malignancy", label: "Malignancy" },
              { value: "Addendum", label: "Addendum" },
              { value: "Correction", label: "Correction" },
            ]}
            value={filters.caseType ?? null}
            onChange={(v) =>
              onFiltersChange({ ...filters, caseType: v ?? undefined })
            }
            clearable
          />
          <Select
            label="TAT"
            placeholder="Any"
            data={[
              { value: "overdue", label: "Overdue" },
              { value: "1", label: "1+ days" },
              { value: "2", label: "2+ days" },
              { value: "3", label: "3+ days" },
              { value: "5", label: "5+ days" },
            ]}
            value={
              filters.tatOverdue
                ? "overdue"
                : filters.tatMinDays != null
                ? String(filters.tatMinDays)
                : null
            }
            onChange={(v) => {
              if (!v) {
                onFiltersChange({
                  ...filters,
                  tatOverdue: undefined,
                  tatMinDays: undefined,
                });
              } else if (v === "overdue") {
                onFiltersChange({
                  ...filters,
                  tatOverdue: true,
                  tatMinDays: undefined,
                });
              } else {
                onFiltersChange({
                  ...filters,
                  tatOverdue: undefined,
                  tatMinDays: parseInt(v, 10),
                });
              }
            }}
            clearable
          />
          <Select
            label="Pending Tests"
            placeholder="All"
            data={[
              { value: "yes", label: "Has Pending Tests" },
              { value: "no", label: "No Pending Tests" },
            ]}
            value={
              filters.hasPendingTests === true
                ? "yes"
                : filters.hasPendingTests === false
                ? "no"
                : null
            }
            onChange={(v) =>
              onFiltersChange({
                ...filters,
                hasPendingTests:
                  v === "yes" ? true : v === "no" ? false : undefined,
              })
            }
            clearable
          />
        </SimpleGrid>
      )}

      {showIncomingFilters && (
        <Select
          label="Current Step"
          placeholder="All steps"
          data={[
            { value: "grossing", label: "Grossing" },
            { value: "embedding", label: "Embedding" },
            { value: "cutting", label: "Cutting" },
            { value: "staining", label: "Staining" },
          ]}
          value={filters.currentStep ?? null}
          onChange={(v) =>
            onFiltersChange({ ...filters, currentStep: v ?? undefined })
          }
          clearable
        />
      )}

      <Stack gap="xs">
        {showAdditionalStainFilter && (
          <AdditionalStainFilter
            filters={filters}
            onFiltersChange={onFiltersChange}
          />
        )}
        <Checkbox
          label="Show only RUSH cases"
          checked={filters.isRush || false}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              isRush: e.currentTarget.checked ? true : undefined,
            })
          }
        />
        <Checkbox
          label="Show only high priority cases"
          checked={filters.isHighPriority || false}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              isHighPriority: e.currentTarget.checked ? true : undefined,
            })
          }
        />
        <Checkbox
          label="Show only blocked cases"
          checked={filters.isBlocked || false}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              isBlocked: e.currentTarget.checked ? true : undefined,
            })
          }
        />
      </Stack>
    </Stack>
  );
}
